"use client";

import Link from "next/link";
import { useState } from "react";
import { Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";

export function ForgotPasswordForm() {
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);
  const [pending, setPending] = useState(false);

  async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setPending(true);

    const data = new FormData(event.currentTarget);

    try {
      const response = await fetch("/api/staff/password/forgot", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: String(data.get("email") ?? "") }),
      });

      if (!response.ok) {
        const body = await response.json().catch(() => null);
        setError(body?.error ?? "Could not send the reset email.");
        setPending(false);
        return;
      }

      setSent(true);
      setPending(false);
    } catch {
      setError("Could not reach the XFit service. Check the connection.");
      setPending(false);
    }
  }

  // 🔴 The confirmation is deliberately vague — "if that address belongs to an account". The
  // backend cannot tell us whether it did (it is enumeration-safe by design), and saying
  // anything more definite here would either be a lie or would leak the answer. Do not
  // "improve" this into "Sent!" — the login screen is public.
  if (sent) {
    return (
      <div className="space-y-4">
        <p role="status" className="rounded-xl bg-card px-4 py-3 text-sm">
          If that address belongs to a staff account, a reset link is on its way. The link works
          once and expires in 30 minutes.
        </p>
        <Link
          href="/login"
          className="block text-center text-sm font-medium text-brand underline underline-offset-4"
        >
          Back to sign in
        </Link>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <div className="space-y-2">
        <label htmlFor="email" className="block text-sm font-medium">
          Email address
        </label>
        <input
          id="email"
          name="email"
          type="email"
          required
          autoComplete="email"
          autoCapitalize="none"
          autoCorrect="off"
          className="h-12 w-full rounded-xl border border-border bg-card px-4 text-base outline-none focus:border-brand"
        />
        <p className="text-sm text-muted-foreground">
          The address on your staff account — not your username.
        </p>
      </div>

      {error ? (
        <p role="alert" className="rounded-xl bg-deny/15 px-4 py-3 text-sm text-deny">
          {error}
        </p>
      ) : null}

      <Button type="submit" size="lg" disabled={pending}>
        {pending ? <Loader2 className="size-5 animate-spin" aria-hidden /> : null}
        {pending ? "Sending…" : "Send reset link"}
      </Button>

      <Link
        href="/login"
        className="block text-center text-sm font-medium text-brand underline underline-offset-4"
      >
        Back to sign in
      </Link>
    </form>
  );
}
